import React from 'react';
import { Grid, TextField, Autocomplete, FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { commonNames, surgeonList, surgeryData, roomNumbers } from '../constants/options';
import { getCurrentDateTime } from '../hooks/useFormValidation';

const PatientAndSurgeryDetails = ({ formData, setFormData, handleChange, handleBlur, validationErrors }) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          label="UHID No"
          value={formData.uhid || ''}
          fullWidth
          InputProps={{ readOnly: true }}
          sx={{ backgroundColor: '#f5f5f5' }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          name="ipNumber"
          label="IP No"
          value={formData.ipNumber || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          fullWidth
          type="number"
          required
          error={!!validationErrors.ipNumber}
          helperText={validationErrors.ipNumber}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          options={commonNames}
          value={formData.name || ''}
          onChange={(e, newValue) => setFormData(prev => ({ ...prev, name: newValue || '' }))}
          renderInput={(params) => (
            <TextField
              {...params}
              name="name"
              label="Patient Name"
              required
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              onBlur={handleBlur}
            />
          )}
          freeSolo
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          name="age"
          label="Age"
          value={formData.age || ''}
          onChange={handleChange}
          fullWidth
          type="number"
          required
          error={!!validationErrors.age}
          helperText={validationErrors.age}
          inputProps={{ min: 0, max: 150 }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          select
          name="sex"
          label="Sex"
          value={formData.sex || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          fullWidth
          required
          error={!!validationErrors.sex}
          helperText={validationErrors.sex}
        >
          <MenuItem value="Male">Male</MenuItem>
          <MenuItem value="Female">Female</MenuItem>
          <MenuItem value="Other">Other</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          select
          label="Room No"
          name="room"
          value={formData.room || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          fullWidth
          required
          error={!!validationErrors.room}
          helperText={validationErrors.room}
        >
          {roomNumbers.map((room) => (
            <MenuItem key={room} value={room}>
              {room}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth required error={!!validationErrors.diagnosis}>
          <InputLabel id="type-of-surgery-label">Type of Surgery/Procedure</InputLabel>
          <Select
            labelId="type-of-surgery-label"
            id="type-of-surgery"
            value={formData.diagnosis || ''}
            label="Type of Surgery/Procedure"
            onChange={(event) =>
              handleChange({
                target: { name: 'diagnosis', value: event.target.value },
              })
            }
          >
            {surgeryData.map((surgery, index) => (
              <MenuItem key={index} value={surgery.diagnosisDescription}>
                {surgery.diagnosisDescription}
              </MenuItem>
            ))}
          </Select>
          {validationErrors.diagnosis && (
             <FormHelperText>{validationErrors.diagnosis}</FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Surgical Code (ICD-10)"
          name="surgical_code"
          value={formData.surgical_code || ''}
          onChange={handleChange}
          required
          fullWidth
          InputProps={{ readOnly: true }}
          helperText="Auto-filled when surgery is selected"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Procedure Code (CPT)"
          name="procedure_code"
          value={formData.procedure_code || ''}
          onChange={handleChange}
          required
          fullWidth
          InputProps={{ readOnly: true }}
          helperText="Auto-filled when surgery is selected"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Procedure Description"
          name="proposedSurgery"
          value={formData.proposedSurgery || ''}
          onChange={handleChange}
          required
          fullWidth
          InputProps={{ readOnly: true }}
          helperText="Auto-filled when surgery is selected"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          select
          label="Primary Surgeon Name"
          name="surgeonName"
          value={formData.surgeonName || ''}
          onChange={handleChange}
          required
          fullWidth
          error={!!validationErrors.surgeonName}
          helperText={validationErrors.surgeonName}
        >
          {surgeonList.map((s) => (
            <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          name="scheduledDatetime"
          type="datetime-local"
          label="Scheduled Date/Time"
          value={formData.scheduledDatetime || ''}
          onChange={handleChange}
          fullWidth
          inputProps={{
            min: getCurrentDateTime(),
          }}
          InputLabelProps={{ shrink: true }}
          error={!!validationErrors.scheduledDatetime}
          helperText={validationErrors.scheduledDatetime || "Surgery must be scheduled for future date/time"}
        />
      </Grid>
    </Grid>
  );
};

export default PatientAndSurgeryDetails;
